// src/components/WeatherThemeBridge.js
import React, { useEffect } from "react";
import { useColorMode } from "theme-ui";
import { useChecklistDefaults } from "../context/ChecklistDefaultsContext";
import { useWeather } from "../context/WeatherContext";

// TOGGLE: if false → bridge + switch behave as dark/light only
//         if true  → bridge allows cycling across all variants
const ENABLE_FULL_VARIANTS = true;

const modeFromDefaults = ({ defaultHazard, seasonalHazard, candidates }) => {
  const hasWildfireCandidate = candidates?.some(
    (c) => c.hazard === "Wildfire"
  );

  if (!hasWildfireCandidate) {
    return "severe";
  }

  if (defaultHazard === "Wildfire") {
    return "wildfire";
  }

  return "severe";
};

// Map hazard to *base* hazard family name
const hazardToMode = (hazard) => {
  if (!hazard) return "severe";

  const h = String(hazard).toLowerCase();

  if (h === "wildfire" || hazard === "Wildfire") return "wildfire";

  if (
    h === "winter-weather" ||
    h === "winterweather" ||
    hazard === "Winter Weather"
  ) {
    return "winter";
  }

  if (h === "flooding" || hazard === "Flooding") return "flooding";

  return "severe";
};

// Extract hazard family from any mode name
// e.g. "winter_light" → "winter", "wildfireDarkHighContrast" → "wildfire"
const getFamily = (mode) => {
  if (!mode) return "severe";
  const m = String(mode);
  if (m.startsWith("winter")) return "winter";
  if (m.startsWith("wildfire")) return "wildfire";
  if (m.startsWith("flooding")) return "flooding";
  return "severe";
};

const WeatherThemeBridge = () => {
  const { defaultHazard, seasonalHazard, candidates } = useChecklistDefaults();
  const weatherCtx = useWeather();
  const weather = weatherCtx?.weather;

  const [colorMode, setColorMode] = useColorMode();

  // 1) Base hazard family ("severe" | "winter" | "wildfire" | "flooding")
  const hazardBase =
    weather != null && weather !== ""
      ? hazardToMode(weather)
      : modeFromDefaults({ defaultHazard, seasonalHazard, candidates });

  useEffect(() => {
    const currentFamily = getFamily(colorMode);

    console.log("[WeatherThemeBridge]", {
      weather,
      defaultHazard,
      seasonalHazard,
      candidates,
      colorMode,
      hazardBase,
      currentFamily,
      ENABLE_FULL_VARIANTS,
    });

    // If hazard family changed (e.g. winter → wildfire),
    // snap to that family's *default* variant.
    if (hazardBase && hazardBase !== currentFamily) {
      // For both dark/light mode and full variant mode, the base is the plain dark:
      // "severe", "winter", "wildfire", "flooding"
      setColorMode(hazardBase);
    }

    // If family is unchanged, do nothing:
    // - when ENABLE_FULL_VARIANTS is false, the switch logic only toggles
    //   between base and "<base>_light" anyway.
    // - when ENABLE_FULL_VARIANTS is true, the switch can freely move among
    //   all variants for this family, and we don't override it here.
  }, [
    weather,
    defaultHazard,
    seasonalHazard,
    candidates,
    hazardBase,
    colorMode,
    setColorMode,
  ]);

  return null;
};

export default WeatherThemeBridge;
